import { useCallback, useEffect, useState } from 'react';

import {
  ApiError,
  clearPulseOverride,
  getPulse,
  overridePulse,
  type Pulse as PulseData,
  type SignalKey,
  type SignalStatus,
} from '../api.js';
import { formatDate } from '../labels.js';

/**
 * The Project Pulse (PR-02).
 *
 * `06` sets five things this screen has to do, and calls them the explanation
 * contract. If any is missing, the screen is not finished:
 *
 *   1. WHICH SIGNAL is driving the overall status
 *   2. WHICH RECORDS are driving that signal — by name
 *   3. WHICH RULE fired, in plain language
 *   4. WHEN it was calculated
 *   5. WHETHER an override is in force, by whom, why, and until when
 *
 * All five are here, and the fifth is the one with teeth: an override shows the
 * status it is hiding and keeps every driver listed underneath it. An override
 * that concealed what it overrode would be a way to make a project look fine,
 * which is the opposite of the point.
 *
 * There is deliberately no number between 0 and 100 anywhere on it.
 */

const STATUS_WORDS: Readonly<Record<SignalStatus, string>> = {
  green: 'On track',
  amber: 'At risk',
  red: 'Critical',
  unknown: 'Not measured',
};

const STATUS_SYMBOLS: Readonly<Record<SignalStatus, string>> = {
  green: '✓',
  amber: '▲',
  red: '■',
  unknown: '○',
};

const SIGNAL_NAMES: Readonly<Record<SignalKey, string>> = {
  schedule: 'Schedule',
  approvals: 'Approvals',
  procurement: 'Procurement',
  site: 'Site',
  quality: 'Quality',
  commercial: 'Commercial',
};

const MIN_REASON = 20;
const MAX_DAYS = 30;

interface Props {
  readonly projectId: string;
  readonly can: { readonly override: boolean };
}

export function Pulse({ projectId, can }: Props): JSX.Element {
  const [pulse, setPulse] = useState<PulseData | null>(null);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [overriding, setOverriding] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setPulse(await getPulse(projectId));
      setDenied(false);
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not work out the project health.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: () => Promise<PulseData>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setPulse(await work());
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (denied) {
    return (
      <>
        <h3 className="sub-head">Health</h3>
        <p className="hint">Your role does not include this project&rsquo;s health.</p>
      </>
    );
  }

  if (!pulse) {
    return (
      <>
        <h3 className="sub-head">Health</h3>
        {error ? (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        ) : (
          <p aria-live="polite">Working out the project health…</p>
        )}
      </>
    );
  }

  const worst = pulse.signals.find(
    (s) => s.status === pulse.overall && s.status !== 'green' && s.status !== 'unknown',
  );

  return (
    <>
      <div className="section-head sub-head-row">
        <h3 className="sub-head">Health</h3>
        <button type="button" className="link-button" disabled={busy} onClick={() => void load()}>
          Work it out again
        </button>
      </div>

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      <div className={`pulse-overall pulse-${pulse.overall}`} role="status">
        <p className="pulse-verdict">
          <span className="pulse-symbol" aria-hidden="true">
            {STATUS_SYMBOLS[pulse.overall]}
          </span>{' '}
          <strong>{STATUS_WORDS[pulse.overall]}</strong>
          {/* (1) which signal is driving it */}
          {worst && <> — driven by {SIGNAL_NAMES[worst.key]}</>}
        </p>

        <p className="hint">
          {/* (4) when it was calculated */}
          Worked out just now, from {pulse.measured} of 6 signals.
          {pulse.unmeasured.length > 0 && (
            <>
              {' '}
              {pulse.unmeasured.map((k) => SIGNAL_NAMES[k]).join(' and ')}{' '}
              {pulse.unmeasured.length === 1 ? 'could not be' : 'could not be'} measured — which is
              not the same as being fine.
            </>
          )}
          {pulse.previous && pulse.previous.overall !== pulse.overall && (
            <> Previously {STATUS_WORDS[pulse.previous.overall].toLowerCase()}.</>
          )}
        </p>

        {/* (5) whether an override is in force, by whom, why, until when */}
        {pulse.override && (
          <p className="pulse-override-note">
            <strong>
              {STATUS_WORDS[pulse.computed_overall]} — shown as{' '}
              {STATUS_WORDS[pulse.override.target_status].toLowerCase()} by{' '}
              {pulse.override.created_by_name ?? 'someone'} until{' '}
              {formatDate(pulse.override.expires_at)}:
            </strong>{' '}
            {pulse.override.reason}
          </p>
        )}
      </div>

      <ul className="pulse-signals">
        {pulse.signals.map((s) => (
          <li key={s.key} className={`pulse-signal pulse-${s.status}`}>
            <div className="pulse-row">
              <span className="pulse-name">
                <span className="pulse-symbol" aria-hidden="true">
                  {STATUS_SYMBOLS[s.status]}
                </span>{' '}
                {SIGNAL_NAMES[s.key]}
              </span>
              <span className="pulse-status">{STATUS_WORDS[s.status]}</span>
              <span className="pulse-count">
                {s.not_computable
                  ? s.not_computable
                  : s.drivers.length === 0
                    ? 'Nothing outstanding'
                    : `${s.drivers.length} ${s.drivers.length === 1 ? 'thing' : 'things'} driving it`}
              </span>
              <span className="row-actions">
                {s.drivers.length > 0 && (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setOpen(open === s.key ? null : s.key)}
                  >
                    {open === s.key ? 'Hide' : 'Why'}
                    <span className="visually-hidden"> is {SIGNAL_NAMES[s.key]} {STATUS_WORDS[s.status]}</span>
                  </button>
                )}
                {can.override && s.override && (
                  <button
                    type="button"
                    className="link-button"
                    disabled={busy}
                    onClick={() => void run(() => clearPulseOverride(projectId, s.key))}
                  >
                    Lift the override
                    <span className="visually-hidden"> on {SIGNAL_NAMES[s.key]}</span>
                  </button>
                )}
                {can.override && !s.override && s.status !== 'green' && s.status !== 'unknown' && (
                  <button
                    type="button"
                    className="link-button"
                    disabled={busy}
                    onClick={() => {
                      setError(null);
                      setOverriding(s.key);
                    }}
                  >
                    Override
                    <span className="visually-hidden"> {SIGNAL_NAMES[s.key]}</span>
                  </button>
                )}
              </span>
            </div>

            {/* An override never hides what it overrode. */}
            {s.override && (
              <p className="pulse-override-note">
                <strong>
                  {STATUS_WORDS[s.computed_status]} — shown as{' '}
                  {STATUS_WORDS[s.override.target_status].toLowerCase()} by{' '}
                  {s.override.created_by_name ?? 'someone'} until{' '}
                  {formatDate(s.override.expires_at)}:
                </strong>{' '}
                {s.override.reason}
              </p>
            )}

            {open === s.key && (
              <ul className="pulse-drivers">
                {/* (2) which records, by name · (3) which rule, in plain language */}
                {s.drivers.map((d, index) => (
                  <li key={`${d.entity_type}-${d.entity_id ?? index}`}>
                    <strong>{d.reference}</strong> <span className="pulse-why">{d.why}</span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>

      {overriding && (
        <OverrideForm
          signal={overriding}
          name={SIGNAL_NAMES[overriding as SignalKey] ?? 'Overall'}
          computed={
            pulse.signals.find((s) => s.key === overriding)?.computed_status ?? pulse.overall
          }
          busy={busy}
          onCancel={() => {
            setOverriding(null);
            setError(null);
          }}
          onSubmit={async (input) => {
            if (await run(() => overridePulse(projectId, { signal: overriding, ...input })))
              setOverriding(null);
          }}
        />
      )}

      <p className="hint">
        There is no score out of a hundred here, and there will not be one. There is a colour, the
        records that produced it, and when it was worked out.
      </p>
    </>
  );
}

// ---------------------------------------------------------------------------

interface OverrideProps {
  readonly signal: string;
  readonly name: string;
  readonly computed: SignalStatus;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    status: 'green' | 'amber';
    reason: string;
    expires_on: string;
  }) => void | Promise<void>;
}

function OverrideForm({
  signal,
  name,
  computed,
  busy,
  onCancel,
  onSubmit,
}: OverrideProps): JSX.Element {
  const [status, setStatus] = useState<'green' | 'amber'>('green');
  const [reason, setReason] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const latest = new Date(Date.now() + MAX_DAYS * 86_400_000).toISOString().slice(0, 10);
  const [expires, setExpires] = useState(
    new Date(Date.now() + 14 * 86_400_000).toISOString().slice(0, 10),
  );

  return (
    <div className="panel">
      <p className="panel-title">Override {name}</p>
      <p className="hint">
        {name} computes as <strong>{STATUS_WORDS[computed].toLowerCase()}</strong>. An override
        says you know, and that it is better than it looks — it can never make something look
        worse. <strong>It does not hide anything:</strong> the reasons stay on the screen
        underneath, and your name and your reason go on the record and into the weekly report.
      </p>

      <div className="field-row">
        <div className="field">
          <label htmlFor="pulse-status">Show it as</label>
          <select
            id="pulse-status"
            value={status}
            disabled={busy}
            onChange={(e) => setStatus(e.target.value as 'green' | 'amber')}
          >
            <option value="green">On track</option>
            <option value="amber">At risk</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="pulse-expires">Until</label>
          <input
            id="pulse-expires"
            type="date"
            value={expires}
            min={today}
            max={latest}
            disabled={busy}
            onChange={(e) => setExpires(e.target.value)}
          />
          <p className="hint">
            {MAX_DAYS} days at most. It reverts by itself — an override with no end is a decision
            to stop measuring.
          </p>
        </div>
      </div>

      <div className="field">
        <label htmlFor="pulse-reason">Why is it better than it computes?</label>
        <textarea
          id="pulse-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={2000}
          disabled={busy}
          placeholder="Client agreed the revised handover date in writing on 9 August."
        />
        <p className="hint">
          {reason.trim().length}/{MIN_REASON} characters minimum.
        </p>
      </div>

      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || reason.trim().length < MIN_REASON || !expires}
          onClick={() => void onSubmit({ status, reason: reason.trim(), expires_on: expires })}
        >
          {busy ? 'Saving…' : `Show ${signal === 'overall' ? 'the project' : name} as ${status === 'green' ? 'on track' : 'at risk'}`}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
